/* eslint-disable camelcase */
import { OptionType, SORT_ORDER } from '@evinced-private/ui-common';

import { lowercaseFirstLetter } from 'src/common/helpers/FormatHelper';
import { buildTupleString } from 'src/common/helpers/GQLRequestsHelper/buildTupleString';
import { QueryVariablesBuilder } from 'src/common/helpers/GQLRequestsHelper/QueryVariablesBuilder';
import { DevelopmentToggles, getSingleToggle } from 'src/common/services/TogglesService';
import {
	ConditionOperators,
	Wsdk_Test_RunsSortFields,
	Wsdk_Test_RunsSortOrder,
	Wsdk_Test_RunsTextSearchFields
} from 'src/common/types/graphql.generated';

import { IGetWebSdkTestsParams } from '../../../../types/WebSdkDashboardServiceTypes';
import { TestsQueryVariables } from '../Tests.generated';

const DAYS_AMOUNT_FILTER_PARAM_KEY = 'daysAmountFilter';
const PROPERTIES_FILTER_PARAM_KEY = 'propertiesFilter';

const enableWebSdkProperties = getSingleToggle(DevelopmentToggles.ENABLE_WEB_SDK_PROPERTIES);

const testsSortFieldNamesMap: Record<string, Wsdk_Test_RunsSortFields> = {
	testName: Wsdk_Test_RunsSortFields.TestName,
	runTime: Wsdk_Test_RunsSortFields.RunTime,
	criticalIssues: Wsdk_Test_RunsSortFields.CriticalIssues,
	totalIssues: Wsdk_Test_RunsSortFields.TotalIssues,
	testPath: Wsdk_Test_RunsSortFields.TestPath,
	testDisplayName: Wsdk_Test_RunsSortFields.TestName
};

const testsSortOrderMap: Record<SORT_ORDER, Wsdk_Test_RunsSortOrder> = {
	[SORT_ORDER.ASC]: Wsdk_Test_RunsSortOrder.Ascending,
	[SORT_ORDER.DESC]: Wsdk_Test_RunsSortOrder.Descending
};

export const testsSearchFields = [
	Wsdk_Test_RunsTextSearchFields.TestName,
	Wsdk_Test_RunsTextSearchFields.TotalIssues,
	Wsdk_Test_RunsTextSearchFields.CriticalIssues,
	Wsdk_Test_RunsTextSearchFields.RunTime
];

export const labelsSearchFields = [
	Wsdk_Test_RunsTextSearchFields.Environments,
	Wsdk_Test_RunsTextSearchFields.Flows,
	Wsdk_Test_RunsTextSearchFields.CustomFields
];

/**
 * Converts labels to tuple strings for custom_fields filter
 * All labels go into a single array for OR behavior
 */
const buildCustomFieldsTuples = (labels: OptionType[]): string[] => {
	const tuples: string[] = [];

	labels.forEach((label) => {
		const labelValueStr = label.value as string;
		const colonIndex = labelValueStr.indexOf(': ');
		if (colonIndex <= 0) {
			return;
		}

		const labelName = labelValueStr.substring(0, colonIndex);
		const labelValue = labelValueStr.substring(colonIndex + 2);
		const normalizedKey = lowercaseFirstLetter(labelName);

		// Add original tuple
		tuples.push(buildTupleString(normalizedKey, labelValue));

		// Add lowercase value variant for case-insensitive matching
		const normalizedValue = labelValue.toLowerCase();
		if (normalizedValue !== labelValue) {
			tuples.push(buildTupleString(normalizedKey, normalizedValue));
		}
	});

	return tuples;
};

export const buildLabelsSearchValues = (labels: OptionType[]): string[] =>
	labels.map(({ value: filterValue }) => {
		const valueStr = filterValue as string;
		const colonIndex = valueStr.indexOf(': ');
		if (colonIndex <= 0) {
			return valueStr;
		}
		const labelName = valueStr.substring(0, colonIndex);
		const labelValue = valueStr.substring(colonIndex + 2);
		const normalizedKey = lowercaseFirstLetter(labelName);
		return buildTupleString(normalizedKey, labelValue);
	});

const buildBaseTestsQueryVariables = ({
	pageSize,
	page,
	filterOptions,
	sort,
	isShowTestPath
}: IGetWebSdkTestsParams): QueryVariablesBuilder<TestsQueryVariables> => {
	const variablesBuilder = new QueryVariablesBuilder<TestsQueryVariables>({
		searchFields: testsSearchFields,
		sortFieldsMap: testsSortFieldNamesMap,
		sortOrderMap: testsSortOrderMap
	});

	let sortFields;
	if (sort?.dataField === 'testDisplayName') {
		sortFields = isShowTestPath ? ['testPath', 'testName'] : ['testName'];
	}

	variablesBuilder
		.addPaginationParams(page, pageSize)
		.addSortParam(sort, sortFields)
		.addTextSearchParam(
			filterOptions?.searchValue ?? '',
			isShowTestPath ? [...testsSearchFields, Wsdk_Test_RunsTextSearchFields.TestPath] : undefined
		);

	if (filterOptions?.testPeriod?.length && filterOptions.testPeriod[0].value !== 'all') {
		variablesBuilder.addConditionParam(
			DAYS_AMOUNT_FILTER_PARAM_KEY,
			filterOptions.testPeriod[0].value
		);
	}

	if (enableWebSdkProperties) {
		variablesBuilder.addFilterParam(PROPERTIES_FILTER_PARAM_KEY, filterOptions?.properties);
	}

	return variablesBuilder;
};

export const buildTestsQueryVariables = ({
	pageSize,
	page,
	filterOptions,
	sort,
	isShowTestPath
}: IGetWebSdkTestsParams): TestsQueryVariables => {
	const variablesBuilder = buildBaseTestsQueryVariables({
		pageSize,
		page,
		filterOptions,
		sort,
		isShowTestPath
	});

	const labels = filterOptions?.labels ?? [];
	if (labels.length) {
		variablesBuilder.addTextSearchParam(buildLabelsSearchValues(labels), labelsSearchFields, true);
	}

	return variablesBuilder.getVariables();
};

export const buildTestsQueryVariablesV2 = ({
	pageSize,
	page,
	filterOptions,
	sort,
	isShowTestPath
}: IGetWebSdkTestsParams): TestsQueryVariables => {
	const variablesBuilder = buildBaseTestsQueryVariables({
		pageSize,
		page,
		filterOptions,
		sort,
		isShowTestPath
	});

	const labels = filterOptions?.labels ?? [];
	const baseVariables = variablesBuilder.getVariables();

	if (labels.length) {
		const tuples = buildCustomFieldsTuples(labels);
		if (tuples.length > 0) {
			return {
				...baseVariables,
				customFieldsFilter: {
					value: [tuples], // All tuples in one array for OR behavior
					operator: ConditionOperators.All
				}
			} as TestsQueryVariables;
		}
	}

	return baseVariables;
};
